import { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield, KeyRound, Mail, Smartphone, Users, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PinInput = ({ value, onChange, testId, placeholder = '••••••' }) => (
  <input
    data-testid={testId}
    type="password"
    inputMode="numeric"
    maxLength={6}
    value={value}
    onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))}
    placeholder={placeholder}
    className="w-full px-4 py-3 rounded-xl bg-black/30 border border-purple-500/30 text-white text-center text-xl tracking-[0.4em] focus:outline-none focus:ring-2 focus:ring-violet-500"
  />
);

const AccountSecurity = () => {
  const { token, user } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [pinStatus, setPinStatus] = useState({ has_pin: false, locked: false });
  const [twoFA, setTwoFA] = useState({ enabled: false });
  const [assistants, setAssistants] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState({});

  // PIN forms
  const [newPin, setNewPin] = useState('');
  const [pinPassword, setPinPassword] = useState('');
  const [curPin, setCurPin] = useState('');
  const [changePin, setChangePin] = useState('');
  const [showChangePin, setShowChangePin] = useState(false);
  const [resetOtp, setResetOtp] = useState('');
  const [resetNewPin, setResetNewPin] = useState('');
  const [showReset, setShowReset] = useState(false);

  // Email change
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [emailPin, setEmailPin] = useState('');

  // 2FA
  const [twoFAPassword, setTwoFAPassword] = useState('');
  const [twoFAOtp, setTwoFAOtp] = useState('');
  const [twoFAStep, setTwoFAStep] = useState('idle'); // idle | confirm

  // Assistants
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');

  const isAuthor = user?.user_type === 'author' || user?.user_type === 'editorial';

  useEffect(() => {
    refreshAll();
  }, []);

  const refreshAll = async () => {
    try {
      const [p, t] = await Promise.all([
        axios.get(`${API}/security/pin/status`, { headers }),
        axios.get(`${API}/security/2fa/status`, { headers }),
      ]);
      setPinStatus(p.data);
      setTwoFA(t.data);
    } catch (e) { /* ignore */ }
    if (isAuthor) fetchAssistants();
  };

  const fetchAssistants = async () => {
    try {
      const res = await axios.get(`${API}/security/assistants`, { headers });
      setAssistants(res.data.assistants || []);
      setPendingInvites(res.data.pending_invites || []);
    } catch (e) { /* ignore */ }
  };

  const setBusy = (k, v) => setLoading((s) => ({ ...s, [k]: v }));
  const errMsg = (e, fb) => {
    const d = e.response?.data?.detail;
    return typeof d === 'string' ? d : fb;
  };

  // ---- PIN ----
  const handleSetPin = async () => {
    if (!/^\d{6}$/.test(newPin)) return toast.error('El PIN debe tener 6 dígitos');
    if (!pinPassword) return toast.error('Ingresa tu contraseña');
    setBusy('setpin', true);
    try {
      await axios.post(`${API}/security/pin/set`, { password: pinPassword, pin: newPin }, { headers });
      toast.success('PIN creado correctamente');
      setNewPin(''); setPinPassword('');
      refreshAll();
    } catch (e) { toast.error(errMsg(e, 'Error al crear el PIN')); }
    finally { setBusy('setpin', false); }
  };

  const handleChangePin = async () => {
    if (!/^\d{6}$/.test(changePin)) return toast.error('El nuevo PIN debe tener 6 dígitos');
    setBusy('changepin', true);
    try {
      await axios.post(`${API}/security/pin/change`, { current_pin: curPin, new_pin: changePin }, { headers });
      toast.success('PIN actualizado');
      setCurPin(''); setChangePin(''); setShowChangePin(false);
    } catch (e) { toast.error(errMsg(e, 'Error al cambiar el PIN')); }
    finally { setBusy('changepin', false); }
  };

  const handleResetRequest = async () => {
    setBusy('resetreq', true);
    try {
      await axios.post(`${API}/security/pin/reset/request`, {}, { headers });
      toast.success('Enviamos un código a tu correo');
      setShowReset(true);
    } catch (e) { toast.error(errMsg(e, 'Error al solicitar el código')); }
    finally { setBusy('resetreq', false); }
  };

  const handleResetConfirm = async () => {
    if (!/^\d{6}$/.test(resetNewPin)) return toast.error('El nuevo PIN debe tener 6 dígitos');
    setBusy('resetconf', true);
    try {
      await axios.post(`${API}/security/pin/reset/confirm`, { otp: resetOtp, new_pin: resetNewPin }, { headers });
      toast.success('PIN restablecido');
      setResetOtp(''); setResetNewPin(''); setShowReset(false);
    } catch (e) { toast.error(errMsg(e, 'Error al restablecer el PIN')); }
    finally { setBusy('resetconf', false); }
  };

  // ---- Email change ----
  const handleEmailChange = async () => {
    if (!newEmail || !emailPassword) return toast.error('Completa correo y contraseña');
    if (!/^\d{6}$/.test(emailPin)) return toast.error('Ingresa tu PIN de 6 dígitos');
    setBusy('email', true);
    try {
      const res = await axios.post(`${API}/security/email/change/request`,
        { new_email: newEmail, password: emailPassword, pin: emailPin }, { headers });
      toast.success(res.data.message || 'Revisa tu correo para confirmar');
      setNewEmail(''); setEmailPassword(''); setEmailPin('');
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d === 'pin_not_set') toast.error('Primero crea tu PIN de retiro arriba.');
      else toast.error(errMsg(e, 'Error al solicitar el cambio de correo'));
    }
    finally { setBusy('email', false); }
  };

  // ---- 2FA ----
  const handle2FAEnableReq = async () => {
    if (!twoFAPassword) return toast.error('Ingresa tu contraseña');
    setBusy('2fa', true);
    try {
      await axios.post(`${API}/security/2fa/enable/request`, { password: twoFAPassword }, { headers });
      toast.success('Enviamos un código a tu correo');
      setTwoFAStep('confirm');
    } catch (e) { toast.error(errMsg(e, 'Error al activar 2FA')); }
    finally { setBusy('2fa', false); }
  };

  const handle2FAEnableConfirm = async () => {
    if (!/^\d{6}$/.test(twoFAOtp)) return toast.error('Ingresa el código de 6 dígitos');
    setBusy('2fa', true);
    try {
      await axios.post(`${API}/security/2fa/enable/confirm`, { otp: twoFAOtp }, { headers });
      toast.success('Verificación en dos pasos activada');
      setTwoFAStep('idle'); setTwoFAPassword(''); setTwoFAOtp('');
      refreshAll();
    } catch (e) { toast.error(errMsg(e, 'Código incorrecto')); }
    finally { setBusy('2fa', false); }
  };

  const handle2FADisable = async () => {
    if (!twoFAPassword) return toast.error('Ingresa tu contraseña para desactivar');
    setBusy('2fa', true);
    try {
      await axios.post(`${API}/security/2fa/disable`, { password: twoFAPassword }, { headers });
      toast.success('Verificación en dos pasos desactivada');
      setTwoFAPassword('');
      refreshAll();
    } catch (e) { toast.error(errMsg(e, 'Error al desactivar 2FA')); }
    finally { setBusy('2fa', false); }
  };

  // ---- Assistants ----
  const handleInvite = async () => {
    if (!inviteEmail || !inviteName) return toast.error('Completa nombre y correo');
    setBusy('invite', true);
    try {
      await axios.post(`${API}/security/assistants/invite`, { email: inviteEmail, full_name: inviteName }, { headers });
      toast.success(`Invitación enviada a ${inviteEmail}`);
      setInviteEmail(''); setInviteName('');
      fetchAssistants();
    } catch (e) { toast.error(errMsg(e, 'Error al enviar la invitación')); }
    finally { setBusy('invite', false); }
  };

  const handleRemoveAssistant = async (id) => {
    if (!window.confirm('¿Desactivar este asistente? Perderá el acceso de inmediato.')) return;
    try {
      await axios.delete(`${API}/security/assistants/${id}`, { headers });
      toast.success('Asistente desactivado');
      fetchAssistants();
    } catch (e) { toast.error(errMsg(e, 'Error al desactivar')); }
  };

  const inputCls = "w-full px-4 py-3 rounded-xl bg-black/30 border border-purple-500/30 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-violet-500";

  return (
    <div className="space-y-6" data-testid="account-security-section">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
          <Shield className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="gotham-bold text-xl text-white">Seguridad de la Cuenta</h2>
          <p className="verdana-regular text-white/60 text-sm">Protege tu identidad, tus datos bancarios y tus ganancias</p>
        </div>
      </div>

      {/* PIN */}
      <Card className="card-viubux-dark border-purple-500/20" data-testid="pin-card">
        <CardHeader>
          <CardTitle className="gotham-bold text-white flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-violet-400" /> PIN de Retiro (6 dígitos)
          </CardTitle>
          <CardDescription className="text-white/70">
            Requerido para modificar datos bancarios, cambiar tu correo y solicitar pagos.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!pinStatus.has_pin ? (
            <div className="space-y-3">
              <label className="text-white/80 text-sm">Crea tu PIN</label>
              <PinInput value={newPin} onChange={setNewPin} testId="new-pin-input" />
              <input data-testid="pin-password-input" type="password" value={pinPassword}
                onChange={(e) => setPinPassword(e.target.value)} placeholder="Tu contraseña" className={inputCls} />
              <Button data-testid="create-pin-btn" onClick={handleSetPin} disabled={loading.setpin} className="btn-viubux w-full">
                {loading.setpin ? 'Creando...' : 'Crear PIN de Retiro'}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-green-400 text-sm bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                ✓ PIN configurado y activo{pinStatus.locked ? ' (temporalmente bloqueado por intentos fallidos)' : ''}
              </div>
              <div className="flex flex-wrap gap-3">
                <Button data-testid="toggle-change-pin-btn" variant="outline" onClick={() => setShowChangePin((v) => !v)}
                  className="border-purple-500/40 text-white/80 hover:bg-purple-500/10">Cambiar PIN</Button>
                <Button data-testid="reset-pin-btn" variant="outline" onClick={handleResetRequest} disabled={loading.resetreq}
                  className="border-orange-500/40 text-orange-300 hover:bg-orange-500/10">
                  {loading.resetreq ? 'Enviando...' : 'Olvidé mi PIN'}
                </Button>
              </div>

              {showChangePin && (
                <div className="space-y-3 bg-black/30 rounded-xl p-4" data-testid="change-pin-form">
                  <label className="text-white/80 text-sm">PIN actual</label>
                  <PinInput value={curPin} onChange={setCurPin} testId="current-pin-input" />
                  <label className="text-white/80 text-sm">Nuevo PIN</label>
                  <PinInput value={changePin} onChange={setChangePin} testId="change-pin-input" />
                  <Button data-testid="confirm-change-pin-btn" onClick={handleChangePin} disabled={loading.changepin} className="btn-viubux w-full">
                    {loading.changepin ? 'Guardando...' : 'Guardar Nuevo PIN'}
                  </Button>
                </div>
              )}

              {showReset && (
                <div className="space-y-3 bg-black/30 rounded-xl p-4" data-testid="reset-pin-form">
                  <label className="text-white/80 text-sm">Código enviado a tu correo</label>
                  <PinInput value={resetOtp} onChange={setResetOtp} testId="reset-otp-input" placeholder="Código" />
                  <label className="text-white/80 text-sm">Nuevo PIN</label>
                  <PinInput value={resetNewPin} onChange={setResetNewPin} testId="reset-new-pin-input" />
                  <Button data-testid="confirm-reset-pin-btn" onClick={handleResetConfirm} disabled={loading.resetconf} className="btn-viubux w-full">
                    {loading.resetconf ? 'Restableciendo...' : 'Restablecer PIN'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Change Email */}
      <Card className="card-viubux-dark border-purple-500/20" data-testid="change-email-card">
        <CardHeader>
          <CardTitle className="gotham-bold text-white flex items-center gap-2">
            <Mail className="w-5 h-5 text-violet-400" /> Cambiar Correo Electrónico
          </CardTitle>
          <CardDescription className="text-white/70">
            Requiere tu contraseña y tu PIN. Enviaremos un enlace de confirmación al nuevo correo y un aviso de seguridad al actual.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-white/60 text-sm">Correo actual: <span className="text-white font-medium">{user?.email}</span></div>
          <input data-testid="new-email-input" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
            placeholder="Nuevo correo" className={inputCls} />
          <input data-testid="email-password-input" type="password" value={emailPassword} onChange={(e) => setEmailPassword(e.target.value)}
            placeholder="Tu contraseña" className={inputCls} />
          <div>
            <label className="text-white/80 text-sm mb-1 block">PIN de retiro</label>
            <PinInput value={emailPin} onChange={setEmailPin} testId="email-pin-input" />
          </div>
          <Button data-testid="request-email-change-btn" onClick={handleEmailChange} disabled={loading.email} className="btn-viubux w-full">
            {loading.email ? 'Enviando...' : 'Solicitar Cambio de Correo'}
          </Button>
        </CardContent>
      </Card>

      {/* 2FA */}
      <Card className="card-viubux-dark border-purple-500/20" data-testid="twofa-card">
        <CardHeader>
          <CardTitle className="gotham-bold text-white flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-violet-400" /> Verificación en Dos Pasos (2FA)
          </CardTitle>
          <CardDescription className="text-white/70">
            Al iniciar sesión te pediremos un código enviado a tu correo.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className={`text-sm rounded-lg p-3 border ${twoFA.enabled ? 'text-green-400 bg-green-500/10 border-green-500/30' : 'text-white/60 bg-black/30 border-white/10'}`}>
            {twoFA.enabled ? '✓ 2FA activado' : '2FA desactivado'}
          </div>
          {!twoFA.enabled ? (
            twoFAStep === 'idle' ? (
              <div className="space-y-3">
                <input data-testid="twofa-password-input" type="password" value={twoFAPassword} onChange={(e) => setTwoFAPassword(e.target.value)}
                  placeholder="Tu contraseña" className={inputCls} />
                <Button data-testid="enable-2fa-btn" onClick={handle2FAEnableReq} disabled={loading['2fa']} className="btn-viubux w-full">
                  {loading['2fa'] ? 'Enviando...' : 'Activar 2FA'}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <label className="text-white/80 text-sm">Código enviado a tu correo</label>
                <PinInput value={twoFAOtp} onChange={setTwoFAOtp} testId="twofa-otp-input" placeholder="Código" />
                <Button data-testid="confirm-2fa-btn" onClick={handle2FAEnableConfirm} disabled={loading['2fa']} className="btn-viubux w-full">
                  {loading['2fa'] ? 'Verificando...' : 'Confirmar y Activar'}
                </Button>
              </div>
            )
          ) : (
            <div className="space-y-3">
              <input data-testid="twofa-disable-password-input" type="password" value={twoFAPassword} onChange={(e) => setTwoFAPassword(e.target.value)}
                placeholder="Tu contraseña" className={inputCls} />
              <Button data-testid="disable-2fa-btn" variant="outline" onClick={handle2FADisable} disabled={loading['2fa']}
                className="w-full border-red-500/40 text-red-300 hover:bg-red-500/10">
                {loading['2fa'] ? 'Procesando...' : 'Desactivar 2FA'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Assistants - authors only */}
      {isAuthor && (
        <Card className="card-viubux-dark border-purple-500/20" data-testid="assistants-card">
          <CardHeader>
            <CardTitle className="gotham-bold text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-violet-400" /> Asistentes / Editores
            </CardTitle>
            <CardDescription className="text-white/70">
              Invita a un asistente para gestionar tus obras, marketing, subidas y mensajes. No podrá cambiar tu usuario, correo, datos bancarios ni solicitar pagos.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <input data-testid="invite-name-input" value={inviteName} onChange={(e) => setInviteName(e.target.value)}
                placeholder="Nombre del asistente" className={inputCls} />
              <input data-testid="invite-email-input" type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="Correo del asistente" className={inputCls} />
            </div>
            <Button data-testid="invite-assistant-btn" onClick={handleInvite} disabled={loading.invite} className="btn-viubux">
              <Plus className="w-4 h-4 mr-1" /> {loading.invite ? 'Enviando...' : 'Enviar Invitación'}
            </Button>

            {(assistants.length > 0 || pendingInvites.length > 0) && (
              <div className="space-y-2 mt-2" data-testid="assistants-list">
                {assistants.map((a) => (
                  <div key={a.id} className="flex items-center justify-between bg-black/30 rounded-lg p-3 border border-white/10">
                    <div>
                      <p className="text-white text-sm font-medium">{a.full_name}</p>
                      <p className="text-white/50 text-xs">{a.email} · {a.is_active === false ? 'Inactivo' : 'Editor activo'}</p>
                    </div>
                    <Button data-testid={`remove-assistant-${a.id}`} variant="ghost" size="sm" onClick={() => handleRemoveAssistant(a.id)}
                      className="text-red-400 hover:bg-red-500/10">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                {pendingInvites.map((i) => (
                  <div key={i.id} className="flex items-center justify-between bg-black/20 rounded-lg p-3 border border-yellow-500/20">
                    <div>
                      <p className="text-white text-sm font-medium">{i.full_name}</p>
                      <p className="text-yellow-300/70 text-xs">{i.email} · Invitación pendiente</p>
                    </div>
                    <span className="text-yellow-400/60 text-xs">Pendiente</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AccountSecurity;
